import { CreditCard } from 'lucide-react';
import applePayMark from '@/assets/apple-pay-mark.svg';
import googlePayMark from '@/assets/google-pay-mark.svg';
import type { PaymentMethodId } from './availability';

/**
 * The mark shown above each payment method.
 *
 * The two wallet marks are files rather than drawn here on purpose. Apple and Google both publish the
 * artwork for their payment marks and both require it to be used as supplied - Apple's Marketing Guidelines
 * specifically prohibit redrawing, recolouring or reconstructing theirs - so the component's job is to place
 * an asset, never to imitate one. Swapping in the official files means replacing the contents of
 * `src/assets/{apple,google}-pay-mark.svg` and touching nothing else.
 *
 * Both are rendered at a fixed height with the width left to follow, because the two badges are not the
 * same shape - Apple's is 165.52107x105.9651 (1.56:1) and Google's 752x400 (1.88:1). `h-7` is the slot;
 * `w-auto` is what lets each keep its own proportion, and what lets a replacement file drop in without a
 * layout change.
 *
 * Check the viewBox on any artwork you swap in - it decides the aspect ratio an <img> is given, and neither
 * vendor ships one that suits a slot directly. Apple's is cropped to the mark; Google's carries its clear
 * space inside the canvas (752x400 of artwork in 1094x742), which renders the pill at 15px beside a 28px
 * Apple mark until the canvas is cropped. See the header comment in each file.
 *
 * `alt=""` on both: the visible label beside the mark already names the method, and a screen reader
 * announcing "Apple Pay image, Apple Pay" is noise.
 */
const WALLET_MARKS: Partial<Record<PaymentMethodId, string>> = {
    applePay: applePayMark,
    googlePay: googlePayMark,
};

export function PaymentMark({ method }: { method: PaymentMethodId }) {
    const mark = WALLET_MARKS[method];

    // `self-start` so the badge sits against the left edge like the card glyph, rather than being stretched
    // and centred by the column that holds it.
    if (mark) return <img src={mark} alt="" className="h-7 w-auto self-start" />;

    return <CreditCard className="size-7" strokeWidth={1.5} aria-hidden="true" />;
}
